import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getToken } from 'firebase/messaging';
import { getFirebaseMessaging } from '../services/firebase';
import { saveFcmToken } from '../services/notificationApi';

/**
 * Inscrit ce navigateur aux notifications push FCM (même onglet/app fermé —
 * voir le document "Notifications & FCM"). Ne fait rien si :
 *  - la config Firebase n'est pas renseignée (.env) — désactivé proprement
 *  - le navigateur ne supporte pas les notifications push
 *  - l'utilisateur n'est pas connecté
 * Ne redemande la permission qu'une fois par session (registered ref), comme
 * useNotifications() pour le WebSocket.
 */
export function useFcmToken() {
  const token = useSelector((state) => state.user?.accessToken);
  const registered = useRef(false);

  useEffect(() => {
    if (!token || registered.current) return;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

    let cancelled = false;

    async function register() {
      const messaging = await getFirebaseMessaging();
      if (!messaging || cancelled) {
        // eslint-disable-next-line no-console
        console.warn('[FCM] Désactivé : config Firebase absente (variables REACT_APP_FIREBASE_* non présentes au build) ou navigateur non supporté.');
        return;
      }

      try {
        const permission =
          Notification.permission === 'default'
            ? await Notification.requestPermission()
            : Notification.permission;

        if (permission !== 'granted') return;

        await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        // .register() résout dès l'enregistrement créé, pas une fois le SW
        // réellement actif (installation → activation prennent un instant) :
        // PushManager.subscribe(), utilisé en interne par getToken(), exige un
        // SW actif et échoue sinon ("no active Service Worker"). .ready
        // n'aboutit qu'une fois cette activation terminée.
        const activeRegistration = await navigator.serviceWorker.ready;

        const fcmToken = await getToken(messaging, {
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
          serviceWorkerRegistration: activeRegistration,
        });

        if (fcmToken && !cancelled) {
          await saveFcmToken(fcmToken);
          registered.current = true;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[FCM] Inscription au push impossible :', error?.message || error);
      }
    }

    register();

    return () => {
      cancelled = true;
    };
  }, [token]);
}
